import type { GaugesResponse, NftV4ResponseItem } from "../validators"
import type { SafelyFetchFn } from "./safely-fetch"
import type { UrlParams } from "./types"
import {
  clsetResponseSchema,
  gaugeBribesResponseSchema,
  gaugesResponseSchema,
  individualPairSchema,
  pairResponseSchema,
  rewardsByNftResponseSchema,
  scaleMinimalPairsResponseSchema,
  statsEqualizerResponseSchema,
  statsTopPairsAprsResponseSchema,
  tokenResponseSchema,
  veNftV4ResponseSchema,
} from "../validators"

export class ApiService {
  private readonly baseUrl: string
  private readonly safelyFetch: SafelyFetchFn

  constructor({ baseUrl, safelyFetch }: UrlParams & { safelyFetch: SafelyFetchFn }) {
    this.baseUrl = baseUrl
    this.safelyFetch = safelyFetch
  }

  makePath = (path: string) => `${this.baseUrl}${path}`

  getTokens = async () => {
    const response = await this.safelyFetch({
      url: this.makePath("/v1/original/v4/tokens"),
      options: {
        method: "get",
      },
      returnOnError: [],
    })

    const parsedResponse = tokenResponseSchema.safeParse(response)

    if (!parsedResponse.success) {
      return {}
    }

    return parsedResponse.data
  }

  getClSets = async () => {
    const response = await this.safelyFetch({
      url: this.makePath("/v1/original/v4/clsets"),
      options: {
        method: "get",
      },
      returnOnError: [],
    })

    const parsedResponse = clsetResponseSchema.safeParse(response)

    if (!parsedResponse.success) {
      return {}
    }

    return parsedResponse.data
  }

  getV4ActivePairs = async () => {
    const response = await this.safelyFetch({
      url: this.makePath("/v1/original/v4/pairs"),
      options: {
        method: "get",
      },
      returnOnError: [],
    })

    const parsedResponse = pairResponseSchema.safeParse(response)

    if (!parsedResponse.success) {
      return {}
    }

    return parsedResponse.data
  }

  /**
   * Get all minimal pairs from API - 'cl-farm' tags are already filtered in the API
   * @returns
   */
  getAllMinimalRealTimePairs = async () => {
    const response = await this.safelyFetch({
      url: this.makePath("/v1/original/scale/pairs/all/minimal"),
      options: {
        method: "get",
      },
      returnOnError: [],
    })

    const parsedResponse = scaleMinimalPairsResponseSchema.safeParse(response)

    // console.log("parsedResponse", parsedResponse)

    if (!parsedResponse.success) {
      return []
    }
    return parsedResponse.data
  }

  getGauges = async () => {
    const response = await this.safelyFetch({
      url: this.makePath("/v1/original/v4/gauges"),
      options: {
        method: "get",
      },
      returnOnError: [],
    })

    const parsedResponse = gaugesResponseSchema.safeParse(response)

    if (!parsedResponse.success) {
      return {}
    }
    return parsedResponse.data as GaugesResponse
  }

  getGaugeBribes = async () => {
    const response = await this.safelyFetch({
      url: this.makePath("/v1/original/v4/gauges/bribes"),
      options: {
        method: "get",
      },
      returnOnError: [],
    })

    const parsedResponse = gaugeBribesResponseSchema.safeParse(response)

    if (!parsedResponse.success) {
      return {}
    }

    return parsedResponse.data
  }

  getV4Pair = async (address: string) => {
    const response = await this.safelyFetch({
      url: this.makePath(`/v1/original/v4/pairs/${address}`),
      options: {
        method: "get",
      },
      returnOnError: [],
    })

    const parsedResponse = individualPairSchema.safeParse(response)

    if (!parsedResponse.success) {
      return null
    }

    return parsedResponse.data
  }

  getVestNFTs = async (account: string): Promise<Array<NftV4ResponseItem>> => {
    const response = await this.safelyFetch({
      url: this.makePath(`/v1/original/v4/wallet/${account}/nfts`),
      options: {
        method: "get",
      },
      returnOnError: [],
    })

    const parsedResponse = veNftV4ResponseSchema.safeParse(response)

    if (!parsedResponse.success) {
      return [] as Array<NftV4ResponseItem>
    }
    return parsedResponse.data
  }

  getSpecificNftRewards = async (nftId: number) => {
    // console.log(`getSpecificNftRewards`, nftId)
    const response = await this.safelyFetch({
      url: this.makePath(`/v1/original/v4/nft/rewards/${nftId}`),
      options: {
        method: "get",
      },
      returnOnError: [],
    })

    // if (nftId === 16706) {
    //   console.log(`response-${nftId}`, response)
    // }

    const parsedResponse = rewardsByNftResponseSchema.safeParse(response)
    // if (nftId === 16706) {
    //   console.log(`parsedResponse-${nftId}`, parsedResponse)
    // }
    if (!parsedResponse.success) {
      return {}
    }
    return parsedResponse.data
  }

  getStatsEqualizer = async () => {
    const response = await this.safelyFetch({
      url: this.makePath("/v1/original/stats/scale"),
      options: {
        method: "get",
      },
      returnOnError: [],
    })

    const parsedResponse = statsEqualizerResponseSchema.safeParse(response)
    if (!parsedResponse.success) {
      return {}
    }

    return parsedResponse.data
  }

  getStatsTopPairAprs = async () => {
    const response = await this.safelyFetch({
      url: this.makePath("/v1/original/stats/top-pair-aprs"),
      options: {
        method: "get",
      },
      returnOnError: [],
    })

    const parsedResponse = statsTopPairsAprsResponseSchema.safeParse(response)
    if (!parsedResponse.success) {
      return []
    }

    return parsedResponse.data
  }

  getV4EmissionsAnalytics = async () =>
    this.safelyFetch({
      url: this.makePath("/v1/original/v4/analytics/emissions"),
      options: {
        method: "get",
      },
      returnOnError: [],
    })

  getV4VolumeAnalytics = async () =>
    this.safelyFetch({
      url: this.makePath("/v1/original/v4/analytics/volume"),
      options: {
        method: "get",
      },
      returnOnError: [],
    })
}
